import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import Alert from 'view/components/Alert';
import { FormInput, FormSelect } from 'view/components/Form';
import { closeModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import { useAdminUsers } from 'data/repositories/adminUsersRepository';
import { useAdminSubscriptions } from 'data/repositories/adminSubscriptionsRepository';
import { isValidEmail } from 'utils/validators';
import { UserHelper } from 'view/helpers';

const FreeTrialOnboardingModal = ({ options }) => {
	const dispatch = useDispatch();
	const { onSuccess } = options || {};

	// Get users for assignment (Violerts admins like Allen)
	const {
		items: users,
		isLoading: isLoadingUsers,
		actions: userActions,
	} = useAdminUsers();

	// Get subscription actions
	const { actions: subscriptionActions } = useAdminSubscriptions();

	// Form state
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { isSubmitting },
	} = useForm({
		defaultValues: {
			existing_user_id: '',
			name: '',
			email: '',
			phone: '',
			portfolio_name: '',
			portfolio_company: '',
			property_ids: [],
			assigned_admin_id: '',
			assigned_admin_role: 'admin',
			custom_message: '',
		},
		mode: 'onChange',
	});

	const [error, setError] = useState(null);
	const [isExistingUser, setIsExistingUser] = useState(false);

	// Watch form values
	const watchEmail = watch('email');

	// Fetch users on mount
	useEffect(() => {
		userActions.fetchPaginated(1, 1000);
	}, [userActions]);

	// Check if user exists when email changes
	useEffect(() => {
		const checkUserExists = async () => {
			if (watchEmail && isValidEmail(watchEmail)) {
				const existingUser = users.find(
					(u) => u.email.toLowerCase() === watchEmail.toLowerCase()
				);
				setIsExistingUser(!!existingUser);
				if (existingUser) {
					setValue('existing_user_id', existingUser.id);
					setValue('name', existingUser.name);
					setValue('phone', existingUser.phone || '');
				} else {
					setValue('existing_user_id', '');
				}
			}
		};
		checkUserExists();
	}, [watchEmail, users, setValue]);

	// Form submission
	const onSubmit = async (data) => {
		try {
			setError(null);

			// Prepare payload
			const payload = {
				existing_user_id: data.existing_user_id || null,
				name: data.name,
				email: data.email,
				phone: data.phone || null,
				portfolio_id: null,
				portfolio_name: data.portfolio_name,
				portfolio_company: data.portfolio_company || null,
				property_ids: data.property_ids || [],
				assigned_admin_id: data.assigned_admin_id || null,
				assigned_admin_role: data.assigned_admin_role || 'admin',
				custom_message: data.custom_message || null,
			};

			// Call API via repository
			const response = await subscriptionActions.onboardFreeTrial({
				data: payload,
			});

			if (response) {
				dispatch(
					showToast({
						type: 'success',
						title: 'Success!',
						body: 'Free trial client onboarded successfully.',
						duration: 5000,
					})
				);

				if (onSuccess) {
					onSuccess(response);
				}

				dispatch(closeModal());
			}
		} catch (err) {
			setError(
				err?.response?.data?.message ||
					err?.message ||
					'An error occurred during onboarding.'
			);
		}
	};

	const handleClose = () => {
		dispatch(closeModal());
	};

	// Violerts admin options (filter for admin/employee users - Violerts team)
	const violertsAdminOptions = users
		.filter(UserHelper.isViolertsAdminUser)
		.map((u) => ({
			value: u.id,
			label: `${u.name} (${u.email})`,
		}));

	const roleOptions = [
		{ value: 'viewer', label: 'Viewer' },
		{ value: 'admin', label: 'Admin' },
	];

	return (
		<StandardModal
			title="Free Trial Client Onboarding"
			onClose={handleClose}
			size="lg"
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="">
					{isLoadingUsers ? (
						<div className="text-center py-4">
							<Spinner />
							<p className="mt-3 text-muted">Loading data...</p>
						</div>
					) : (
						<>
							{error && (
								<Alert type="danger" className="mb-3">
									{error}
								</Alert>
							)}

							<Alert type="info" className="mb-4">
								<i className="bi bi-info-circle me-2"></i>
								<strong>Free Trial:</strong> Notifications will be disabled by
								default. The client will receive a welcome email to activate
								their account.
							</Alert>

							{/* User Information Section */}
							<div className="mb-4">
								<h6 className="mb-3">
									<i className="bi bi-person-circle me-2"></i>
									Client Information
								</h6>

								<FormInput
									name="email"
									control={control}
									type="email"
									label="Email Address"
									placeholder="Enter client email"
									required
									validate={{
										isValidEmail: (value) =>
											isValidEmail(value) ||
											'Please enter a valid email address',
									}}
									autoFocus
									disabled={isSubmitting}
								/>

								{isExistingUser && (
									<Alert type="info" className="mb-3">
										<i className="bi bi-info-circle me-2"></i>
										User with this email already exists. Existing account will
										be used.
									</Alert>
								)}

								{!isExistingUser && (
									<>
										<FormInput
											name="name"
											control={control}
											label="Full Name"
											placeholder="Enter client name"
											required
											disabled={isSubmitting}
										/>

										<FormInput
											name="phone"
											control={control}
											label="Phone Number"
											placeholder="(555) 555-5555"
											disabled={isSubmitting}
										/>
									</>
								)}
							</div>

							{/* Portfolio Section */}
							<div className="mb-4">
								<h6 className="mb-3">
									<i className="bi bi-folder me-2"></i>
									Portfolio Setup
								</h6>

								<FormInput
									name="portfolio_name"
									control={control}
									label="Portfolio Name"
									placeholder="Enter portfolio name"
									required
									disabled={isSubmitting}
								/>

								<FormInput
									name="portfolio_company"
									control={control}
									label="Company/Organization"
									placeholder="Enter company name"
									disabled={isSubmitting}
								/>

								<div className="form-text">
									<i className="bi bi-info-circle me-1"></i>
									Properties can be added after onboarding or during portfolio
									creation.
								</div>
							</div>

							{/* Violerts Admin Assignment */}
							<div className="mb-4">
								<h6 className="mb-3">
									<i className="bi bi-shield-check me-2"></i>
									Assign Violerts Admin (Optional)
								</h6>

								<div className="row">
									<div className="col-md-8">
										<FormSelect
											name="assigned_admin_id"
											control={control}
											label="Violerts Team Member"
											options={violertsAdminOptions}
											placeholder="Select admin (e.g., Allen)"
											disabled={isSubmitting}
											helpText="Assign a Violerts team member to this client's portfolio."
										/>
									</div>
									<div className="col-md-4">
										<FormSelect
											name="assigned_admin_role"
											control={control}
											label="Role"
											options={roleOptions}
											disabled={isSubmitting}
										/>
									</div>
								</div>
							</div>

							{/* Custom Email Message */}
							<div className="mb-4">
								<h6 className="mb-3">
									<i className="bi bi-envelope me-2"></i>
									Welcome Email
								</h6>

								<FormInput
									name="custom_message"
									control={control}
									label="Custom Message (Optional)"
									type="textarea"
									rows={3}
									placeholder="Add a personalized message to the welcome email..."
									maxLength={2000}
									showCharCount
									disabled={isSubmitting}
									helpText="This message will be included in the welcome email sent to the client."
								/>
							</div>
						</>
					)}
				</div>

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleClose}
						disabled={isSubmitting}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-success"
						disabled={isSubmitting || isLoadingUsers}
					>
						{isSubmitting ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
								/>
								Onboarding...
							</>
						) : (
							<>
								<i className="bi bi-check-circle me-2" />
								Onboard Free Trial Client
							</>
						)}
					</button>
				</div>
			</form>
		</StandardModal>
	);
};

export default FreeTrialOnboardingModal;
